import { Injectable, NgZone } from '@angular/core';
import { fromEvent, merge, Observable, Subscription } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import {
  Router,
  NavigationEnd,
  NavigationStart,
  NavigationCancel,
} from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class ActivityTrackerService {
  private activityLog: string[] = [];
  private originalConsole: any = {};
  private originalSubscribe: any;

  constructor(private ngZone: NgZone, private router: Router) {
    // this.startTracking();
    this.trackNavigation();
    this.hookConsole();
    // this.hookRxSubscribe();
    this.hookLocalStorage();
    // this.hookNetwork();
  }

  // 🔹 Track user interactions
  private startTracking() {
    this.ngZone.runOutsideAngular(() => {
      const events = [
        fromEvent(document, 'click'),
        fromEvent(document, 'keydown'),
        // fromEvent(document, 'mousemove'),
        // fromEvent(document, 'scroll'),
      ];

      merge(...events)
        .pipe(
          debounceTime(300),
          map((event: Event) => this.describeEvent(event))
        )
        .subscribe((desc) => {
          this.addLog(desc);
        });
    });
  }

  // 🔹 Track navigation + redirects
  private trackNavigation() {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.addLog(`NavigationStart → ${event.url}`);
      }
      if (event instanceof NavigationCancel) {
        this.addLog(`NavigationCancel → ${event.url}`);
      }
      if (event instanceof NavigationEnd) {
        this.addLog(`NavigationEnd → ${event.urlAfterRedirects}`);
      }
    });
  }

  // 🔹 Track console logs
  private hookConsole() {
    const levels: (keyof Console)[] = ['log', 'warn', 'error', 'info'];
    levels.forEach((level) => {
        // Save original method
        this.originalConsole[level] = console[level];

        // Overwrite with a wrapper
        (console as any)[level] = (...args: any[]) => {
        this.addLog(
            `Console.${level}: ${args.map((a) => {
            try {
                return JSON.stringify(a);
            } catch {
                return String(a);
            }
            }).join(' ')}`
        );
        // Call original method
        this.originalConsole[level].apply(console, args);
        };
    });
  }


  // 🔹 Track RxJS subscriptions
  private hookRxSubscribe() {
    this.originalSubscribe = Observable.prototype.subscribe;

    const tracker = this;
    Observable.prototype.subscribe = function (...args: any[]): Subscription {
      tracker.addLog(`RxJS Subscription → ${this.constructor.name}`);

      const subscription: Subscription = tracker.originalSubscribe.apply(this, args);

      if (args[0] && typeof args[0] === 'function') {
        const originalNext = args[0];
        args[0] = function (value: any) {
          tracker.addLog(`RxJS next → ${JSON.stringify(value)}`);
          return originalNext.call(this, value);
        };
      }

      subscription.add(() => {
        tracker.addLog('RxJS Subscription unsubscribed');
      });

      return subscription;
    };
  }

  

  /**
   * Checks whether a localStorage key holds a live secret (token/session value),
   * as opposed to non-secret token metadata (timestamps, scopes).
   *
   * @param key - The key to check.
   * @returns True if the key's value must never be logged in full.
   */
  private isSensitiveKey(key: string): boolean {
    const sensitiveKeys: string[] = [
      "access_token",
      "id_token",
      "id_token_claims_obj",
      "nonce",
      "refresh_token",
      "session_state"
    ];
    return sensitiveKeys.includes(key);
  }

  private trimToLength(str: string, length: number): string {
    return str.length > length ? str.slice(0, length) : str;
  }

  private hookLocalStorage() {
    const originalSetItem = localStorage.setItem;
    const originalRemoveItem = localStorage.removeItem;
    const originalClear = localStorage.clear;

    // Track setItem
    localStorage.setItem = (key: string, value: string) => {
        if (this.isSensitiveKey(key)){
          this.addLog(`LocalStorage SET → ${key}: [REDACTED]`);
        }else{
          this.addLog(`LocalStorage SET → ${key}: ${this.trimToLength(value, 500)}`);
        }
        return originalSetItem.apply(localStorage, [key, value]);
    };

    // Track removeItem
    localStorage.removeItem = (key: string) => {
        this.addLog(`LocalStorage REMOVE → ${key}`);
        return originalRemoveItem.apply(localStorage, [key]);
    };

    // Track clear
    localStorage.clear = () => {
        this.addLog('LocalStorage CLEAR');
        return originalClear.apply(localStorage);
    };

    // Track cross-tab changes
    window.addEventListener('storage', (event: StorageEvent) => {
        if (event.storageArea === localStorage) {
          if (event.key && this.isSensitiveKey(event.key)){
            this.addLog(`LocalStorage STORAGE EVENT → ${event.key}: [REDACTED]`);
          }else{
            this.addLog(`LocalStorage STORAGE EVENT → ${event.key}: ${this.trimToLength(event.newValue ?? '', 500)}`);
          }
        }
    });
    }
  
  private hookNetwork() {
  // ---- fetch ----
  // const originalFetch = window.fetch.bind(window) as any; // <--- use any here

  // window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
  //   const start = Date.now();
  //   const method = init?.method || 'GET';
  //   const body = init?.body ? ' Body: ' + JSON.stringify(init.body) : '';

  //   this.addLog(`Fetch Request → ${input.toString()} ${method}${body}`);

  //   try {
  //     const response: Response = await originalFetch(input, init);
  //     const elapsed = Date.now() - start;

  //     const clone = response.clone();
  //     let text = '';
  //     try { text = await clone.text(); if (text.length > 500) text = text.slice(0, 500) + '...'; } catch {}

  //     this.addLog(`Fetch Response → ${input.toString()} [${elapsed}ms]: ${text}`);
  //     return response;
  //   } catch (err) {
  //     this.addLog(`Fetch Error → ${input.toString()}: ${err}`);
  //     // throw err;
  //   }
  // };

  // // ---- XHR patch (same as before) ----
  // const originalXhrOpen = XMLHttpRequest.prototype.open;
  // const originalXhrSend = XMLHttpRequest.prototype.send;

  // interface TrackedXHR extends XMLHttpRequest { _method?: string; _url?: string; }

  // XMLHttpRequest.prototype.open = function(this: TrackedXHR, method: string, url: string, async?: boolean, user?: string | null, password?: string | null) {
  //   this._method = method;
  //   this._url = url;
  //   return originalXhrOpen.apply(this, arguments as any);
  // };

  // XMLHttpRequest.prototype.send = function(this: TrackedXHR, body?: Document | BodyInit | null) {
  //   const xhr = this;
  //   const start = Date.now();

  //   const onLoad = function() {
  //     const elapsed = Date.now() - start;
  //     let response = '';
  //     try { response = xhr.responseText; } catch {}
  //     if (response.length > 500) response = response.slice(0, 500) + '...';

  //     (window as any).activityTracker?.addLog(
  //       `XHR Response → ${xhr._method} ${xhr._url} [${elapsed}ms]: ${response}`
  //     );
  //   };

  //   const onError = function() {
  //     (window as any).activityTracker?.addLog(
  //       `XHR Error → ${xhr._method} ${xhr._url}`, true
  //     );
  //   };

  //   xhr.addEventListener('load', onLoad);
  //   xhr.addEventListener('error', onError);

  //   return originalXhrSend.apply(this, arguments as any);
  // };
}




  // 🔹 Public API for logging Auth events & object creation
  logAuthEvent(type: 'login' | 'logout' | 'token-refresh', details?: any) {
    this.addLog(`Auth event → ${type} ${details ? JSON.stringify(details) : ''}`);
  }

  logObjectCreation(className: string, details?: any) {
    this.addLog(`Object created → ${className} ${details ? JSON.stringify(details) : ''}`);
  }

  private describeEvent(event: Event): string {
    if (event.type === 'click') {
      const target = event.target as HTMLElement;
      if (target.tagName.toLowerCase() === 'button') {
        const button = target as HTMLButtonElement;
        return `Button clicked ${button.id} ${button.value}`
      } else {
        console.log('Not a button, clicked element =', target.tagName);
      }
      // const mouse = event as MouseEvent;
      // return `Click at ${mouse.clientX}, ${mouse.clientY}`;
      return ''
    }
    if (event.type === 'keydown') {
      return `Key pressed: ${(event as KeyboardEvent).key}`;
    }
    if (event.type === 'scroll') return 'User scrolled';
    if (event.type === 'mousemove') return 'Mouse moved';
    return event.type;
  }

  public addLog(message: string) {
    this.activityLog.push(`${new Date().toISOString()} - ${message}`);
  }

  getActivityLog() {
    return this.activityLog;
  }

  clearLog() {
    this.activityLog = [];
  }
}
